import React from "react";

const getModeIcon = (mode) => {
  const modeUpper = mode.toUpperCase();
  if (modeUpper === "ORAL") return "🎤";
  if (modeUpper === "POSTER") return "📊";
  if (modeUpper === "VIRTUAL") return "💻";
  if (modeUpper === "HYBRID") return "🔄";
  return "📋";
};

export const AbstractIntentSelector = ({
  hasPaperSubmitted,
  wantsToSubmitAbstract,
  onChooseAbstract,
  onContinueAsDelegate,
}) => (
  <div className="grid-item grid-item--full" style={{ marginBottom: "20px" }}>
    <div
      style={{
        padding: "16px",
        backgroundColor: "#f8f9fa",
        border: "2px solid #dee2e6",
        borderRadius: "8px",
      }}
    >
      <h3
        style={{
          marginTop: 0,
          marginBottom: "15px",
          fontSize: "18px",
          color: "#212529",
        }}
      >
        {hasPaperSubmitted
          ? "Paper/Abstract Submission"
          : "Do you want to submit an abstract?"}
      </h3>
      <div style={{ display: "flex", gap: "15px" }}>
        <button
          type="button"
          onClick={onChooseAbstract}
          style={{
            flex: 1,
            padding: "12px 20px",
            fontSize: "15px",
            fontWeight: "500",
            border:
              wantsToSubmitAbstract === true
                ? "2px solid #28a745"
                : "1px solid #dee2e6",
            backgroundColor:
              wantsToSubmitAbstract === true ? "#d4edda" : "#fff",
            color: wantsToSubmitAbstract === true ? "#155724" : "#495057",
            borderRadius: "6px",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
        >
          {hasPaperSubmitted
            ? "📄 View/Edit Abstract"
            : "✅ Yes, I want to submit an abstract"}
        </button>
        <button
          type="button"
          onClick={onContinueAsDelegate}
          style={{
            flex: 1,
            padding: "12px 20px",
            fontSize: "15px",
            fontWeight: "500",
            border:
              wantsToSubmitAbstract === false
                ? "2px solid #007bff"
                : "1px solid #dee2e6",
            backgroundColor:
              wantsToSubmitAbstract === false ? "#cce5ff" : "#fff",
            color: wantsToSubmitAbstract === false ? "#004085" : "#495057",
            borderRadius: "6px",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
        >
          ℹ️ Continue as delegate
        </button>
      </div>
    </div>
  </div>
);

export const PaymentCompletedNotice = () => (
  <div className="grid-item grid-item--full" style={{ marginBottom: "20px" }}>
    <div
      style={{
        padding: "16px",
        backgroundColor: "#d4edda",
        border: "2px solid #c3e6cb",
        borderRadius: "8px",
        marginBottom: "20px",
      }}
    >
      <h3
        style={{
          marginTop: 0,
          marginBottom: "10px",
          fontSize: "18px",
          color: "#155724",
        }}
      >
        ✓ Payment Completed Successfully
      </h3>
      <p style={{ margin: 0, color: "#155724", fontSize: "15px" }}>
        Thank you for completing your payment. You can now submit your abstract
        below.
      </p>
    </div>
  </div>
);

export const PaperLockedNotice = () => (
  <div className="grid-item grid-item--full" style={{ marginBottom: "15px" }}>
    <div
      style={{
        padding: "12px 16px",
        backgroundColor: "#fff3cd",
        border: "1px solid #ffc107",
        borderRadius: "6px",
        fontSize: "14px",
        color: "#856404",
      }}
    >
      <strong>🔒 Paper Locked:</strong> Your paper is currently in review and
      cannot be edited. If you need to make changes, please contact the
      conference organizers.
    </div>
  </div>
);

const getSubmittedPaperDisplayId = (paper) => paper?.paperId || paper?.id || "-";

const getSubmittedPaperTitle = (paper) =>
  paper?.title || paper?.paperTitle || paper?.name || "-";

const getSubmittedPaperTrack = (paper, tracks = []) => {
  const trackId = paper?.trackId;
  const matchedTrack = tracks.find(
    (track) => String(track.id) === String(trackId)
  );

  return (
    paper?.trackName ||
    paper?.track ||
    paper?.trackTitle ||
    matchedTrack?.name ||
    matchedTrack?.trackName ||
    trackId ||
    "-"
  );
};

const getSubmittedPaperMode = (paper) =>
  paper?.presentationMode || paper?.mode || paper?.paperMode || "-";

const getSubmittedPaperStatusLabel = (status) => {
  const normalized = (status || "PENDING").trim().toUpperCase();
  return (
    {
      DRAFT: "Draft",
      SUBMITTED: "Submitted",
      UNDER_REVIEW: "Under Review",
      REVIEWED: "Reviewed",
      ACCEPTED: "Accepted",
      REJECTED: "Rejected",
      MINOR_REVISION: "Minor Revision",
      MAJOR_REVISION: "Major Revision",
      REVISION_REQUESTED: "Revision Requested",
    }[normalized] || normalized
  );
};

const getSubmittedPaperStatusColor = (status) => {
  const normalized = (status || "PENDING").trim().toUpperCase();
  if (normalized === "ACCEPTED") return "#28a745";
  if (normalized === "REJECTED") return "#dc3545";
  if (normalized === "DRAFT") return "#6c757d";
  if (
    normalized === "MINOR_REVISION" ||
    normalized === "MAJOR_REVISION" ||
    normalized === "REVISION_REQUESTED"
  ) {
    return "#fd7e14";
  }
  return "#17a2b8";
};

const submittedPaperHeaderStyle = {
  padding: "10px",
  textAlign: "left",
  borderBottom: "2px solid #dee2e6",
  color: "#212529",
  backgroundColor: "#f8f9fa",
  fontWeight: "700",
};

const submittedPaperCellStyle = {
  padding: "10px",
  verticalAlign: "top",
  color: "#212529",
};

export const SubmittedPapersCollapsible = ({ submittedPapers, tracks }) => {
  if (!Array.isArray(submittedPapers) || submittedPapers.length === 0) {
    return null;
  }

  return (
    <div className="grid-item grid-item--full">
      <details
        style={{
          marginBottom: "20px",
          border: "1px solid #dee2e6",
          borderRadius: "8px",
          backgroundColor: "#fff",
        }}
      >
        <summary
          style={{
            padding: "14px 16px",
            cursor: "pointer",
            fontWeight: "700",
            color: "#212529",
          }}
        >
          📄 My Papers for this Conference ({submittedPapers.length})
        </summary>
        <div style={{ padding: "0 16px 16px", overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8f9fa" }}>
                <th style={submittedPaperHeaderStyle}>Paper ID</th>
                <th style={submittedPaperHeaderStyle}>Title</th>
                <th style={submittedPaperHeaderStyle}>Track</th>
                <th style={submittedPaperHeaderStyle}>Mode</th>
                <th style={{ ...submittedPaperHeaderStyle, textAlign: "center" }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {submittedPapers.map((paper) => (
                <tr
                  key={paper.id || paper.paperId || paper.title}
                  style={{ borderTop: "1px solid #e9ecef" }}
                >
                  <td style={submittedPaperCellStyle}>
                    {getSubmittedPaperDisplayId(paper)}
                  </td>
                  <td style={submittedPaperCellStyle}>
                    {getSubmittedPaperTitle(paper)}
                  </td>
                  <td style={submittedPaperCellStyle}>
                    {getSubmittedPaperTrack(paper, tracks)}
                  </td>
                  <td style={submittedPaperCellStyle}>
                    {getSubmittedPaperMode(paper)}
                  </td>
                  <td style={{ ...submittedPaperCellStyle, textAlign: "center" }}>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 10px",
                        borderRadius: "12px",
                        backgroundColor: getSubmittedPaperStatusColor(paper.status),
                        color: "#fff",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {getSubmittedPaperStatusLabel(paper.status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
};

export const PaperSubmissionNotice = ({ abstractConfig, maxAbstractChars }) => (
  <div className="grid-item grid-item--full" style={{ marginBottom: "15px" }}>
    <div
      style={{
        padding: "12px 16px",
        backgroundColor: "#e3f2fd",
        border: "1px solid #2196f3",
        borderRadius: "6px",
        fontSize: "14px",
        color: "#0d47a1",
      }}
    >
      <strong>📄 Paper Submission:</strong> Please provide your paper title,
      track, abstract (max {maxAbstractChars} characters), and upload your paper
      file.
      {abstractConfig?.submissionDeadline && (
        <div
          style={{
            marginTop: "8px",
            fontSize: "13px",
            color: "#1565c0",
            fontWeight: "bold",
          }}
        >
          📅 Submission Deadline:{" "}
          {new Date(abstractConfig.submissionDeadline).toLocaleDateString(
            "en-US",
            { year: "numeric", month: "long", day: "numeric" },
          )}
        </div>
      )}
    </div>
  </div>
);

export const PaperTitleField = ({
  paperTitle,
  onChange,
  isPaperLocked,
}) => (
  <div className="grid-item grid-item--full">
    <input
      type="text"
      placeholder="Paper Title *"
      value={paperTitle}
      onChange={(e) => onChange(e.target.value)}
      disabled={isPaperLocked}
      readOnly={isPaperLocked}
      style={{
        width: "100%",
        padding: "10px 14px",
        border: "1px solid #666",
        borderRadius: "8px",
        fontSize: "15px",
        backgroundColor: isPaperLocked ? "#f5f5f5" : "#fff",
        cursor: isPaperLocked ? "not-allowed" : "text",
      }}
    />
  </div>
);

export const TrackSelectField = ({
  selectedTrack,
  tracks,
  onChange,
  isPaperLocked,
}) => (
  <div className="grid-item grid-item--full">
    <select
      value={selectedTrack}
      onChange={(e) => onChange(e.target.value)}
      disabled={isPaperLocked}
      style={{
        width: "100%",
        padding: "10px 14px",
        border: "1px solid #666",
        borderRadius: "8px",
        fontSize: "15px",
        color: selectedTrack ? "#212529" : "#6c757d",
        backgroundColor: isPaperLocked ? "#f5f5f5" : "#fff",
        cursor: isPaperLocked ? "not-allowed" : "pointer",
      }}
    >
      <option value="" disabled>
        Select Track *
      </option>
      {tracks && tracks.length > 0 ? (
        tracks.map((track) => (
          <option key={track.id} value={track.id}>
            {track.name}
          </option>
        ))
      ) : (
        <option value="" disabled>
          Loading tracks...
        </option>
      )}
    </select>
  </div>
);

export const PresentationModeField = ({
  abstractConfig,
  presentationMode,
  onChange,
  isPaperLocked,
}) => {
  const modes = abstractConfig?.mode
    ? abstractConfig.mode.split(",").map((mode) => mode.trim())
    : ["ORAL", "POSTER"];

  return (
    <div className="grid-item grid-item--full">
      <label
        style={{
          display: "block",
          marginBottom: "10px",
          fontWeight: "500",
          color: "#000",
        }}
      >
        Presentation Mode *
      </label>
      <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
        {modes.map((mode) => (
          <label
            key={mode}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: isPaperLocked ? "not-allowed" : "pointer",
              padding: "10px 15px",
              border:
                presentationMode === mode
                  ? "2px solid #007bff"
                  : "1px solid #dee2e6",
              borderRadius: "6px",
              backgroundColor: isPaperLocked
                ? "#f5f5f5"
                : presentationMode === mode
                ? "#e7f3ff"
                : "#fff",
              flex: modes.length === 1 ? "1" : "1 1 calc(50% - 10px)",
              minWidth: "150px",
              transition: "all 0.2s",
              opacity: isPaperLocked ? 0.6 : 1,
            }}
          >
            <input
              type="radio"
              name="presentationMode"
              value={mode}
              checked={presentationMode === mode}
              onChange={(e) => onChange(e.target.value)}
              disabled={isPaperLocked}
              style={{
                marginRight: "8px",
                width: "18px",
                height: "18px",
                cursor: isPaperLocked ? "not-allowed" : "pointer",
              }}
            />
            <span style={{ fontSize: "16px", fontWeight: "400", color: "#000" }}>
              {getModeIcon(mode)} {mode}
            </span>
          </label>
        ))}
      </div>
      <div
        style={{
          marginTop: "12px",
          padding: "12px 14px",
          backgroundColor: "#eaf4ff",
          border: "1px solid #b6dcff",
          borderLeft: "4px solid #0b63c7",
          borderRadius: "8px",
          color: "#0a3d73",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "6px",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "18px",
              height: "18px",
              borderRadius: "50%",
              backgroundColor: "#0b63c7",
              color: "#fff",
              fontSize: "12px",
              fontWeight: "700",
            }}
          >
            i
          </span>
          Presentation Mode Guidance
        </div>
        <div style={{ fontSize: "13px", lineHeight: "1.6" }}>
          Reviewer panel will decide the final mode (Oral or Poster) after
          evaluation.
          <br />
          Select your preference now, and be prepared to present in either
          format.
        </div>
      </div>
      {!presentationMode && modes.length > 1 && (
        <div
          style={{
            marginTop: "8px",
            padding: "8px 12px",
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            fontSize: "13px",
            color: "#856404",
          }}
        >
          ⚠️ Please select one presentation mode from the options above
        </div>
      )}
    </div>
  );
};

export const SubmissionMethodField = ({
  submissionMethod,
  onChange,
  isPaperLocked,
}) => {
  const methods = [
    { value: "TEXTAREA", label: "📝 Text Only" },
    { value: "FILE", label: "📄 File Only" },
    { value: "BOTH", label: "📝📄 Both" },
  ];

  return (
    <div className="grid-item grid-item--full">
      <label
        style={{
          display: "block",
          marginBottom: "10px",
          fontWeight: "500",
          color: "#000",
        }}
      >
        Submission Method *
      </label>
      <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
        {methods.map((method) => (
          <label
            key={method.value}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: isPaperLocked ? "not-allowed" : "pointer",
              padding: "10px 15px",
              border:
                submissionMethod === method.value
                  ? "2px solid #007bff"
                  : "1px solid #dee2e6",
              borderRadius: "6px",
              backgroundColor: isPaperLocked
                ? "#f5f5f5"
                : submissionMethod === method.value
                ? "#e7f3ff"
                : "#fff",
              flex: "1",
              minWidth: "150px",
              transition: "all 0.2s",
              opacity: isPaperLocked ? 0.6 : 1,
            }}
          >
            <input
              type="radio"
              name="submissionMethod"
              value={method.value}
              checked={submissionMethod === method.value}
              onChange={(e) => onChange(e.target.value)}
              disabled={isPaperLocked}
              style={{
                marginRight: "8px",
                width: "18px",
                height: "18px",
                cursor: isPaperLocked ? "not-allowed" : "pointer",
              }}
            />
            <span style={{ fontSize: "15px", color: "#000" }}>
              {method.label}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

export const AbstractTextareaField = ({
  abstractText,
  maxAbstractChars,
  onChange,
  isPaperLocked,
}) => (
  <div className="grid-item grid-item--full">
    <div style={{ position: "relative" }}>
      <textarea
        placeholder={`Structured Abstract * (Maximum ${maxAbstractChars} characters)`}
        value={abstractText}
        onChange={(e) => onChange(e.target.value)}
        maxLength={maxAbstractChars}
        rows={8}
        disabled={isPaperLocked}
        readOnly={isPaperLocked}
        style={{
          width: "100%",
          padding: "10px 14px",
          border:
            abstractText.length > maxAbstractChars
              ? "2px solid #dc3545"
              : "1px solid #666",
          borderRadius: "8px",
          fontSize: "15px",
          backgroundColor: isPaperLocked ? "#f5f5f5" : "#fff",
          cursor: isPaperLocked ? "not-allowed" : "text",
          resize: "vertical",
          fontFamily: "inherit",
        }}
      />
      <div
        style={{
          marginTop: "5px",
          fontSize: "13px",
          color:
            abstractText.length > maxAbstractChars
              ? "#dc3545"
              : abstractText.length > maxAbstractChars * 0.9
              ? "#ffc107"
              : "#6c757d",
          textAlign: "right",
        }}
      >
        {abstractText.length} / {maxAbstractChars} characters
        {abstractText.length > maxAbstractChars && " (exceeds limit!)"}
      </div>
    </div>
  </div>
);

const getAcceptedDocTypeLabel = (acceptedDocTypes) => {
  if (acceptedDocTypes === "PDF_ONLY") return "PDF only";
  if (acceptedDocTypes === "DOC_ONLY") return "DOC/DOCX only";
  return "PDF, DOC, DOCX";
};

const getAcceptedDocExtensions = (acceptedDocTypes) => {
  if (acceptedDocTypes === "PDF_ONLY") return ".pdf";
  if (acceptedDocTypes === "DOC_ONLY") return ".doc,.docx";
  return ".pdf,.doc,.docx";
};

const getAcceptedMimeTypes = (acceptedDocTypes) => {
  if (acceptedDocTypes === "PDF_ONLY") {
    return {
      allowedTypes: ["application/pdf"],
      errorMessage: "Please upload a PDF file only",
    };
  }
  if (acceptedDocTypes === "DOC_ONLY") {
    return {
      allowedTypes: [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ],
      errorMessage: "Please upload a DOC or DOCX file only",
    };
  }
  return {
    allowedTypes: [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    errorMessage: "Please upload a PDF, DOC, or DOCX file only",
  };
};

const clearPaperFileInput = () => {
  const fileInput = document.querySelector('input[type="file"][accept*=".pdf"]');
  if (fileInput) fileInput.value = "";
};

export const PaperFileUploadField = ({
  abstractConfig,
  maxFileSizeMb,
  paperFileUploaded,
  paperFile,
  paperFileName,
  paperFileError,
  onPaperFileChange,
  onPaperFileErrorChange,
  onUploadedFileDelete,
  onSelectedFileRemove,
  isPaperLocked,
}) => {
  const acceptedDocTypes = abstractConfig?.acceptedDocTypes;

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const { allowedTypes, errorMessage } = getAcceptedMimeTypes(acceptedDocTypes);

    if (!allowedTypes.includes(file.type)) {
      onPaperFileErrorChange(errorMessage);
      onPaperFileChange(null);
      event.target.value = "";
    } else if (file.size > maxFileSizeMb * 1024 * 1024) {
      onPaperFileErrorChange(`File size must be less than ${maxFileSizeMb}MB`);
      onPaperFileChange(null);
      event.target.value = "";
    } else {
      onPaperFileErrorChange("");
      onPaperFileChange(file);
    }
  };

  return (
    <div className="grid-item grid-item--full">
      <label
        style={{
          display: "block",
          marginBottom: "8px",
          fontWeight: "500",
          color: "#000",
        }}
      >
        Upload Paper File * ({getAcceptedDocTypeLabel(acceptedDocTypes)} - Max{" "}
        {maxFileSizeMb}MB)
      </label>
      {paperFileUploaded && !paperFile && (
        <div
          style={{
            marginBottom: "12px",
            padding: "12px",
            backgroundColor: "#d1ecf1",
            border: "1px solid #bee5eb",
            borderRadius: "6px",
            color: "#0c5460",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: "14px",
                fontWeight: "500",
                marginBottom: "4px",
              }}
            >
              📎 Previously Uploaded File
            </div>
            <div style={{ fontSize: "13px" }}>
              {paperFileName || "Paper file uploaded"}
            </div>
            {isPaperLocked && (
              <div
                style={{
                  fontSize: "12px",
                  marginTop: "6px",
                  fontStyle: "italic",
                }}
              >
                File upload is locked while paper is under review
              </div>
            )}
          </div>
          {!isPaperLocked && (
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to delete this file? This action cannot be undone.",
                  )
                ) {
                  onUploadedFileDelete();
                  clearPaperFileInput();
                }
              }}
              style={{
                padding: "6px 12px",
                backgroundColor: "#dc3545",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: "500",
                marginLeft: "10px",
              }}
            >
              🗑️ Delete
            </button>
          )}
        </div>
      )}
      <input
        type="file"
        accept={getAcceptedDocExtensions(acceptedDocTypes)}
        disabled={isPaperLocked}
        onChange={handleFileChange}
        style={{
          width: "100%",
          padding: "10px",
          border: "1px solid #666",
          borderRadius: "8px",
          fontSize: "15px",
          cursor: "pointer",
        }}
      />
      {paperFileError && (
        <div
          style={{
            marginTop: "8px",
            padding: "8px 12px",
            backgroundColor: "#fee",
            border: "1px solid #fcc",
            borderRadius: "4px",
            color: "#c33",
            fontSize: "13px",
          }}
        >
          ⚠️ {paperFileError}
        </div>
      )}
      {paperFile && !paperFileError && (
        <div
          style={{
            marginTop: "10px",
            padding: "12px",
            backgroundColor: "#d4edda",
            border: "1px solid #c3e6cb",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{ fontSize: "14px", color: "#155724", fontWeight: "500" }}
            >
              ✓ File uploaded successfully
            </div>
            <div style={{ fontSize: "13px", color: "#155724", marginTop: "4px" }}>
              {paperFile.name} ({(paperFile.size / 1024).toFixed(2)} KB)
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              onSelectedFileRemove();
              clearPaperFileInput();
            }}
            style={{
              padding: "6px 12px",
              backgroundColor: "#dc3545",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "13px",
              marginLeft: "10px",
            }}
          >
            Remove
          </button>
        </div>
      )}
    </div>
  );
};

const coAuthorInputStyle = ({ hasError, isPaperLocked }) => ({
  padding: "8px 12px",
  border: hasError ? "1px solid #dc3545" : "1px solid #ced4da",
  borderRadius: "4px",
  fontSize: "14px",
  backgroundColor: isPaperLocked ? "#f5f5f5" : "#fff",
  cursor: isPaperLocked ? "not-allowed" : "text",
});

export const CoAuthorsSection = ({
  coAuthors,
  coAuthorErrors = [],
  onAddCoAuthor,
  onUpdateCoAuthor,
  onRemoveCoAuthor,
  isPaperLocked,
}) => (
  <div className="grid-item grid-item--full" style={{ marginTop: "20px" }}>
    <div
      style={{
        padding: "15px",
        backgroundColor: "#f8f9fa",
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <label
          style={{
            fontWeight: "600",
            color: "#000",
            fontSize: "15px",
          }}
        >
          Co-Authors (Optional)
        </label>
        <button
          type="button"
          onClick={onAddCoAuthor}
          disabled={isPaperLocked}
          style={{
            padding: "6px 12px",
            backgroundColor: isPaperLocked ? "#6c757d" : "#28a745",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: isPaperLocked ? "not-allowed" : "pointer",
            fontSize: "13px",
            fontWeight: "500",
            opacity: isPaperLocked ? 0.6 : 1,
          }}
        >
          + Add Co-Author
        </button>
      </div>

      {coAuthors.length === 0 && (
        <div
          style={{
            textAlign: "center",
            padding: "20px",
            color: "#6c757d",
            fontSize: "14px",
          }}
        >
          No co-authors added yet. Click "Add Co-Author" to add one.
        </div>
      )}

      {coAuthors.map((coAuthor, index) => (
        <div
          key={index}
          style={{
            marginBottom: "15px",
            padding: "15px",
            backgroundColor: "white",
            borderRadius: "6px",
            border: "1px solid #dee2e6",
            position: "relative",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "10px",
            }}
          >
            <input
              type="text"
              placeholder="Co-Author Full Name"
              value={coAuthor.name}
              onChange={(e) => onUpdateCoAuthor(index, "name", e.target.value)}
              disabled={isPaperLocked}
              readOnly={isPaperLocked}
              style={coAuthorInputStyle({ isPaperLocked })}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <input
                type="email"
                placeholder="Co-Author Email"
                value={coAuthor.email}
                onChange={(e) => onUpdateCoAuthor(index, "email", e.target.value)}
                disabled={isPaperLocked}
                readOnly={isPaperLocked}
                style={coAuthorInputStyle({
                  hasError: coAuthorErrors[index]?.email,
                  isPaperLocked,
                })}
              />
              {coAuthorErrors[index]?.email && (
                <span
                  style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}
                >
                  {coAuthorErrors[index].email}
                </span>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <input
                type="tel"
                placeholder="Phone Number"
                value={coAuthor.phone || ""}
                onChange={(e) => onUpdateCoAuthor(index, "phone", e.target.value)}
                disabled={isPaperLocked}
                readOnly={isPaperLocked}
                style={coAuthorInputStyle({
                  hasError: coAuthorErrors[index]?.phone,
                  isPaperLocked,
                })}
              />
              {coAuthorErrors[index]?.phone && (
                <span
                  style={{ color: "#dc3545", fontSize: "12px", marginTop: "4px" }}
                >
                  {coAuthorErrors[index].phone}
                </span>
              )}
            </div>
            {/* Specialization and Designation fields hidden as requested */}
            <button
              type="button"
              onClick={() => onRemoveCoAuthor(index)}
              disabled={isPaperLocked}
              style={{
                padding: "8px 12px",
                backgroundColor: isPaperLocked ? "#6c757d" : "#dc3545",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: isPaperLocked ? "not-allowed" : "pointer",
                fontSize: "13px",
                opacity: isPaperLocked ? 0.6 : 1,
              }}
            >
              Remove
            </button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const AbstractSubmissionFields = ({
  abstractConfig,
  maxAbstractChars,
  maxFileSizeMb,
  paperTitle,
  onPaperTitleChange,
  selectedTrack,
  tracks,
  onSelectedTrackChange,
  presentationMode,
  onPresentationModeChange,
  submissionMethod,
  onSubmissionMethodChange,
  abstractText,
  onAbstractTextChange,
  paperFileUploaded,
  paperFile,
  paperFileName,
  paperFileError,
  onPaperFileChange,
  onPaperFileErrorChange,
  onUploadedFileDelete,
  onSelectedFileRemove,
  coAuthors,
  coAuthorErrors,
  onAddCoAuthor,
  onUpdateCoAuthor,
  onRemoveCoAuthor,
  isPaperLocked,
  beforeTrackSelect = null,
  beforeCoAuthors = null,
  submittedPapers = [],
}) => (
  <>
    <SubmittedPapersCollapsible
      submittedPapers={submittedPapers}
      tracks={tracks}
    />

    <PaperSubmissionNotice
      abstractConfig={abstractConfig}
      maxAbstractChars={maxAbstractChars}
    />

    <PaperTitleField
      paperTitle={paperTitle}
      onChange={onPaperTitleChange}
      isPaperLocked={isPaperLocked}
    />

    {beforeTrackSelect}

    <TrackSelectField
      selectedTrack={selectedTrack}
      tracks={tracks}
      onChange={onSelectedTrackChange}
      isPaperLocked={isPaperLocked}
    />

    <PresentationModeField
      abstractConfig={abstractConfig}
      presentationMode={presentationMode}
      onChange={onPresentationModeChange}
      isPaperLocked={isPaperLocked}
    />

    <SubmissionMethodField
      submissionMethod={submissionMethod}
      onChange={onSubmissionMethodChange}
      isPaperLocked={isPaperLocked}
    />

    {(submissionMethod === "TEXTAREA" || submissionMethod === "BOTH") && (
      <AbstractTextareaField
        abstractText={abstractText}
        maxAbstractChars={maxAbstractChars}
        onChange={onAbstractTextChange}
        isPaperLocked={isPaperLocked}
      />
    )}

    {(submissionMethod === "FILE" || submissionMethod === "BOTH") && (
      <PaperFileUploadField
        abstractConfig={abstractConfig}
        maxFileSizeMb={maxFileSizeMb}
        paperFileUploaded={paperFileUploaded}
        paperFile={paperFile}
        paperFileName={paperFileName}
        paperFileError={paperFileError}
        onPaperFileChange={onPaperFileChange}
        onPaperFileErrorChange={onPaperFileErrorChange}
        onUploadedFileDelete={onUploadedFileDelete}
        onSelectedFileRemove={onSelectedFileRemove}
        isPaperLocked={isPaperLocked}
      />
    )}

    {beforeCoAuthors}

    {abstractConfig && abstractConfig.coAuthors === true && (
      <CoAuthorsSection
        coAuthors={coAuthors}
        coAuthorErrors={coAuthorErrors}
        onAddCoAuthor={onAddCoAuthor}
        onUpdateCoAuthor={onUpdateCoAuthor}
        onRemoveCoAuthor={onRemoveCoAuthor}
        isPaperLocked={isPaperLocked}
      />
    )}
  </>
);
